import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { ReactComponent as Logo } from 'assets/images/logo.svg';
import { classnames, isTemporaryUser } from 'helpers/utils';
import { goToPage, RouteName } from 'routes';

import {
  DotsGridIcon,
  ShoppingCart03Icon,
  Users02Icon,
  WrittenLogo,
} from 'assets/icons';
import { Breakpoints, NavbarTabs } from 'common/enums';
import { ModalEditVault } from 'common/modal-edit-vault';
import { appActions, AppContext } from 'context';
import { trackCustomEvent } from 'helpers/analytics';
import { useMediaQuery } from 'hooks/use-media-query';
import { useLocation } from 'react-router-dom';
import { NavbarTab } from '../navbar-tab/navbar-tab';
import styles from './left-section.module.scss';

type LeftSectionProps = {
  user: UserType;
  currentVault?: VaultType;
};

const LeftSection: React.FC<LeftSectionProps> = ({ user, currentVault }) => {
  const { state, dispatch } = useContext(AppContext);
  const [showEditVaultModal, setShowEditVaultModal] = useState(false);

  const [navbarSelected, setNavbarSelected] = useState<NavbarTabs | null>(null);

  const isMobileOrTablet = useMediaQuery(`(max-width: ${Breakpoints.sm}px)`);

  const location = useLocation();

  // Collaborators and Store give way when the bar runs out of room and would
  // touch "New Vault" (design note); the account menu still lists them. The
  // width the full set needs is kept so
  // they come back once it fits again, instead of flickering at the edge.
  const containerRef = useRef<HTMLDivElement>(null);
  const collapsedRef = useRef(false);
  const fullWidth = useRef(0);
  const [tabsCollapsed, setTabsCollapsed] = useState(false);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el || isMobileOrTablet) return undefined;
    const update = () => {
      if (!collapsedRef.current && el.scrollWidth > el.clientWidth) {
        fullWidth.current = el.scrollWidth;
        collapsedRef.current = true;
        setTabsCollapsed(true);
      } else if (collapsedRef.current && el.clientWidth >= fullWidth.current) {
        collapsedRef.current = false;
        setTabsCollapsed(false);
      }
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isMobileOrTablet, state.logged]);

  const handleSuccessEditVaultModal = () => {
    setShowEditVaultModal(false);
    dispatch({
      type: appActions.SET_REFETCH_VAULT,
      refetchVault: true,
    });
    trackCustomEvent('confirmEditVault');
  };

  const handleVaults = () => {
    goToPage(RouteName.Vaults);
  };

  const handleCollaborators = () => {
    goToPage(RouteName.Collaborators);
  };

  const handleStore = () => {
    goToPage(RouteName.Store);
  };

  useEffect(() => {
    if (location.pathname === '/vaults') {
      setNavbarSelected(NavbarTabs.vaults);
    } else if (location.pathname.includes('collaborators')) {
      setNavbarSelected(NavbarTabs.collaborators);
    } else if (location.pathname.includes('store')) {
      setNavbarSelected(NavbarTabs.store);
    } else {
      setNavbarSelected(null);
    }
  }, [location.pathname]);

  const showLinksToMain = () => (
    <div className={styles.containerLinks}>
      <NavbarTab
        tab={NavbarTabs.vaults}
        label="All Vaults"
        selectedTab={navbarSelected!}
        onClick={handleVaults}
        icon={<DotsGridIcon />}
      />
      {!tabsCollapsed && (
        <>
          <NavbarTab
            tab={NavbarTabs.collaborators}
            label="Collaborators"
            selectedTab={navbarSelected!}
            onClick={handleCollaborators}
            icon={<Users02Icon />}
          />
          <NavbarTab
            tab={NavbarTabs.store}
            label="Store"
            selectedTab={navbarSelected!}
            onClick={handleStore}
            icon={<ShoppingCart03Icon />}
          />
        </>
      )}
    </div>
  );

  const showContent = () => {
    if (isTemporaryUser(user)) {
      return (
        <div className={styles.logoAndTexts}>
          <Logo className={styles.logo} />
          <div
            className={classnames(styles.allText, {
              [styles.gsBiggerSize]: !user.companyName,
            })}
          >
            <div className={styles.gs}>
              <div className={styles.generational}>Generational</div>
              <div className={styles.album}>Story</div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className={styles.container} ref={containerRef}>
        <button
          type="button"
          onClick={() => goToPage(RouteName.Vaults)}
          className={styles.logoBlock}
        >
          <span className={styles.wordmark}>
            <WrittenLogo />
          </span>
          {user.companyName && (
            <span className={styles.poweredBy}>
              {'Powered by '}
              <span className={styles.poweredByName}>{user.companyName}</span>
            </span>
          )}
        </button>
        {!isMobileOrTablet && state.logged && showLinksToMain()}
      </div>
    );
  };

  const showEditVaultModalComponent = (vault: VaultType) => (
    <ModalEditVault
      onClose={() => setShowEditVaultModal(false)}
      onSuccess={handleSuccessEditVaultModal}
      vault={vault}
    />
  );

  return (
    <>
      {currentVault &&
        showEditVaultModal &&
        showEditVaultModalComponent(currentVault)}
      {showContent()}
    </>
  );
};

export { LeftSection };
